import { createRoutine, promisifyRoutine } from 'redux-saga-routines';
import { takeLatest, call, put, select } from 'redux-saga/effects';
import { set, get } from 'lodash/fp';
// @ts-ignore ts-migrate(7016) FIXME: Could not find a declaration file for module 'quer... Remove this comment to see the full error message
import queryString from 'query-string';
import commonMessages from '@whitelabel/helpers/messages/commonMsg';

import api, { API_HOST } from '../helpers/api';
import { fnolFormtoAPI } from '../helpers/forms/transform';
import { FNOL_ERRORS, CLAIM_JOURNEY_ID } from '../helpers/constants';
import { bwClaimsJourneyTracking } from '../helpers/analytics';
import {
  ICreateClaim,
  IFNOLSection,
  ICreateClaimResponse,
  IUser,
  IError,
  IBWClaimsJourneyError,
} from '../helpers/types';
import { saveOrDeleteClaimValues } from '../helpers/fnol';

export const getClaim = createRoutine('claims/GET_CLAIM');
export const clearClaims = createRoutine('claims/CLEAR_CLAIMS');
export const getClaimQuestions = createRoutine('claims/GET_CLAIM_QUESTIONS');
export const clearClaimQuestions = createRoutine('claims/CLEAR_CLAIM_QUESTIONS');
export const getClaimQuestionsPromiseRoutine = promisifyRoutine(getClaimQuestions);
export const GET_FNOL = {
  REQUEST: 'claims/GET_FNOL/REQUEST',
  SUCCESS: 'claims/GET_FNOL/SUCCESS',
  FAILURE: 'claims/GET_FNOL/FAILURE',
  FULFILL: 'claims/GET_FNOL/FULFILL',
};

export const getFNOL = async (
  claimTypeID: string | null,
  locale: any,
  dispatch: any,
  bookingID: string | null,
  quoteID: string | null,
  securityToken: string | null,
  user: IUser,
) => {
  if (claimTypeID) {
    try {
      dispatch({ type: GET_FNOL.REQUEST });
      const searchParams = new URLSearchParams({ language: locale });
      // only append security token for non-logged in usecase
      if (securityToken && !user) {
        searchParams.append('security_token', securityToken);
      }
      const isAuthRequired = !!user || !securityToken;
      const { data } = await api.get(
        `${API_HOST}/bookings/${bookingID}/quotes/${quoteID}/claim_types/${claimTypeID}/fnol/?${searchParams}`,
        isAuthRequired,
        null,
      );
      dispatch({ type: GET_FNOL.SUCCESS, payload: data });
    } catch (error) {
      dispatch({ type: GET_FNOL.FAILURE, payload: error });
    } finally {
      dispatch({ type: GET_FNOL.FULFILL });
    }
  }
};

export const createClaim = async ({
  values,
  sections,
  actions: { setStatus, setErrors, setSubmitting },
  setGlobalError,
  locale,
  formatMessage,
  bookingPolicyHolder,
  history,
  securityToken,
  quoteID,
  user,
  bwClaimsJourneyTrackingData: { bookingID, ...otherBWClaimsJourneyTrackingData },
}: ICreateClaim): Promise<void> => {
  try {
    const searchParams = new URLSearchParams({ language: locale });
    // only append security token for non-logged in usecase
    if (securityToken) {
      searchParams.append('security_token', securityToken);
    }
    const formattedValues = fnolFormtoAPI(values, sections, bookingPolicyHolder);
    const body = JSON.stringify(formattedValues);
    const isAuthRequired = !!user || !securityToken;
    const {
      data,
    }: {
      data: ICreateClaimResponse;
    } = await api.post(`${API_HOST}/bookings/${bookingID}/quotes/${quoteID}/claims/?${searchParams}`, isAuthRequired, {
      body,
    });
    setStatus(data);
    bwClaimsJourneyTracking({
      ...otherBWClaimsJourneyTrackingData,
      bookingID,
      completed: true,
      submitted: true,
    });
    sessionStorage.removeItem(CLAIM_JOURNEY_ID);
    saveOrDeleteClaimValues(bookingID, false);
  } catch (error) {
    // @ts-expect-error TS2571: Object is of type 'unknown'.
    let globalErrors = [error.message];
    let errors = {};
    let sectionName: string | undefined;
    let fieldName: string | undefined;
    const bwClaimsJourneyError: IBWClaimsJourneyError = {
      sectionName: null,
      fieldName: null,
      // @ts-expect-error TS2571: Object is of type 'unknown'.
      errorMessage: error.message,
    };

    // @ts-expect-error TS2571: Object is of type 'unknown'.
    if (error.errors) {
      // @ts-expect-error TS2571: Object is of type 'unknown'.
      const errorKey = Object.keys(error.errors)[0];
      const errorKeys = errorKey.split('.');
      [, sectionName, fieldName] = errorKeys;
      globalErrors = [formatMessage(commonMessages.xclaimGlobalError)];
      // @ts-expect-error TS2571: Object is of type 'unknown'.
      errors = set(errorKey, error.errors[errorKey], errors);

      bwClaimsJourneyError.sectionName = sectionName;
      bwClaimsJourneyError.fieldName = fieldName;
      // @ts-expect-error TS2571: Object is of type 'unknown'.
      bwClaimsJourneyError.errorMessage = error.errors[errorKey].toString();
    }

    if (!(error as IError).isJSON) {
      globalErrors = [formatMessage(commonMessages.error)];
    }

    setGlobalError(globalErrors);
    bwClaimsJourneyTracking({
      ...otherBWClaimsJourneyTrackingData,
      bookingID,
      completed: true,
      xclaimValidationError: bwClaimsJourneyError,
    });

    if (!sectionName || sectionName === sections[sections.length - 1].name) {
      setErrors(errors);
      setSubmitting(false);
    } else {
      const sectionIndex = sections.findIndex((section: IFNOLSection) => section.name === sectionName);
      history.go(-sections.length + sectionIndex + 1);
      sessionStorage.setItem(FNOL_ERRORS, JSON.stringify(errors));
    }
  }
};

function* handleGetClaim({ payload: claimReference }: { payload: string; type: string }) {
  try {
    // @ts-expect-error fix type for yield
    const locale = yield select(get('intl.locale'));
    const searchParams = queryString.stringify({ language: locale });
    yield put(getClaim.request());
    // @ts-expect-error fix type for yield
    const claim = yield call(api.get, `${API_HOST}/claims/${claimReference}/?${searchParams}`, true);
    yield put(getClaim.success({ locale, claimReference, claim }));
  } catch (error) {
    yield put(getClaim.failure(error));
  } finally {
    yield put(getClaim.fulfill());
  }
}

function* handleGetClaimQuestions({
  payload: { claimSelectorID, bookingID, quoteID, securityToken },
}: {
  payload: { claimSelectorID: string; bookingID: string; quoteID: string; securityToken: string };
  type: string;
}) {
  try {
    // @ts-expect-error fix type for yield
    const locale = yield select(get('intl.locale'));
    // @ts-expect-error fix type for yield
    const user = yield select(get('user.data'));
    const searchParams = new URLSearchParams({ language: locale });
    // only append security token for non-logged in usecase
    if (securityToken && !user) {
      searchParams.append('security_token', securityToken);
    }
    yield put(getClaimQuestions.request());
    const isAuthRequired = !!user || !securityToken;
    // @ts-expect-error fix type for yield
    const claimQuestionsData = yield call(
      api.get,
      `${API_HOST}/bookings/${bookingID}/quotes/${quoteID}/claim_selectors/${claimSelectorID}/?${searchParams}`,
      isAuthRequired,
    );
    yield put(getClaimQuestions.success(claimQuestionsData));
  } catch (error) {
    yield put(getClaimQuestions.failure(error));
  } finally {
    yield put(getClaimQuestions.fulfill());
  }
}

export function* claimsSaga() {
  yield takeLatest(getClaim.TRIGGER, handleGetClaim);
  yield takeLatest(getClaimQuestions.TRIGGER, handleGetClaimQuestions);
}
